(() => {
	const STORAGE_KEY = 'mellow_discount_popup_state_v2';
	const PREFERENCE_KEY = 'mellow_discount_popup_preference';
	const PHONE_KEY = 'mellow_discount_popup_phone';
	const NAME_KEY = 'mellow_discount_popup_name';
	const DEFAULT_DELAY_MS = 15000; // Show popup after 15 seconds
	const DISMISSED_SHOW_AGAIN_DAYS = 4; // Show again after 4 days if dismissed without claiming
	// DEBUG: set to true to show the popup every time (ignore dismissal + design mode guard)
	const DEBUG_ALWAYS_SHOW = false;

	// Analytics worker endpoint - update this after deploying the worker
	const ANALYTICS_WORKER_URL =
		'https://analytics-worker.support-bd5.workers.dev';

	// Discount code worker endpoint - generates a Shopify discount code on phone submit
	const DISCOUNT_CODE_WORKER_URL =
		'https://discount-code-worker.support-bd5.workers.dev';

	const root = document.documentElement;

	function nowMs() {
		return Date.now();
	}

	function safeParseJson(str) {
		try {
			return JSON.parse(str);
		} catch {
			return null;
		}
	}

	/**
	 * Determines whether to suppress the popup.
	 * - If user has claimed a discount (submitted phone) → never show again
	 * - If user dismissed without claiming → show again after DISMISSED_SHOW_AGAIN_DAYS
	 */
	function shouldSuppressPopup() {
		if (DEBUG_ALWAYS_SHOW) return false;
		const raw = window.localStorage?.getItem(STORAGE_KEY);
		if (!raw) return false;
		const parsed = safeParseJson(raw);
		if (!parsed || typeof parsed !== 'object') return false;

		// If they've claimed a discount, never show again
		if (parsed.claimed === true) return true;

		// If they dismissed without claiming, check if enough time has passed
		if (typeof parsed.dismissedAt === 'number') {
			const daysMs = DISMISSED_SHOW_AGAIN_DAYS * 24 * 60 * 60 * 1000;
			return nowMs() - parsed.dismissedAt < daysMs;
		}

		return false;
	}

	/**
	 * Mark popup as dismissed or claimed.
	 * @param {boolean} claimed - true if user submitted their phone and got a discount
	 */
	function markPopupState(claimed = false) {
		if (DEBUG_ALWAYS_SHOW) return;
		try {
			const state = claimed
				? { claimed: true, claimedAt: nowMs() }
				: { dismissedAt: nowMs(), claimed: false };
			window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
		} catch {
			// ignore
		}
	}

	function savePreference(preference) {
		try {
			window.localStorage?.setItem(PREFERENCE_KEY, preference);
		} catch {
			// ignore
		}
	}

	/**
	 * Track the user's preference selection to analytics worker.
	 * Fire-and-forget: we don't block the UI on this.
	 */
	function trackAnalytics(preference) {
		if (!ANALYTICS_WORKER_URL) return;

		fetch(ANALYTICS_WORKER_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ action: 'track', preference }),
		}).catch(() => {
			// Silently fail - analytics should never break the user experience
		});
	}

	/**
	 * Track phone capture (lead). Fire-and-forget.
	 * Note: this requires the analytics worker to support action: "lead".
	 */
	function trackLead(preference, phone) {
		if (!ANALYTICS_WORKER_URL) return;

		fetch(ANALYTICS_WORKER_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ action: 'lead', preference, phone }),
		}).catch(() => {
			// Silently fail - lead capture should never break the user experience
		});
	}

	/**
	 * Create a Shopify discount code via discount-code-worker.
	 * Returns { ok: true, startsAt, endsAt } on success.
	 * Returns { ok: false, error: "already_claimed", details: "..." } if phone already used.
	 */
	async function createDiscountCode(preference, phone, name) {
		if (!DISCOUNT_CODE_WORKER_URL) return { ok: false, error: 'no_endpoint' };

		const res = await fetch(DISCOUNT_CODE_WORKER_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			// `source` identifies the capture surface (the inline cart card
			// sends 'cart-inline') for claim-source analytics.
			body: JSON.stringify({ preference, phone, name, source: 'popup' }),
		});

		const json = await res.json().catch(() => null);
		if (!json) return { ok: false, error: 'parse_error' };

		// Return the full response so caller can handle specific errors
		return json;
	}

	function getFocusableElements(container) {
		return Array.from(
			container.querySelectorAll(
				"a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])"
			)
		).filter(
			(el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
		);
	}

	function trapFocus(container) {
		const focusables = getFocusableElements(container);
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		if (!first || !last) return () => {};

		function onKeyDown(e) {
			if (e.key !== 'Tab') return;
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}

		container.addEventListener('keydown', onKeyDown);
		return () => container.removeEventListener('keydown', onKeyDown);
	}

	async function copyToClipboard(text) {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}

		// Fallback
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', '');
		textarea.style.position = 'fixed';
		textarea.style.top = '-1000px';
		textarea.style.left = '-1000px';
		document.body.appendChild(textarea);
		textarea.select();
		let ok = false;
		try {
			ok = document.execCommand('copy');
		} catch {
			ok = false;
		}
		document.body.removeChild(textarea);
		return ok;
	}

	function normalizePhone(raw) {
		if (!raw) return { digits: '', cleaned: '' };
		const cleaned = String(raw).trim();
		const digits = cleaned.replace(/\D/g, '');
		return { digits, cleaned };
	}

	function isValidPhone(raw) {
		const { digits } = normalizePhone(raw);
		// E.164 max is 15 digits; US is typically 10. We'll accept a reasonable range.
		return digits.length === 10;
	}

	function savePhone(phone) {
		try {
			window.localStorage?.setItem(PHONE_KEY, phone);
		} catch {
			// ignore
		}
	}

	function saveName(name) {
		try {
			window.localStorage?.setItem(NAME_KEY, name);
		} catch {
			// ignore
		}
	}

	function isValidName(raw) {
		if (!raw) return false;
		const trimmed = String(raw).trim();
		return trimmed.length >= 2;
	}

	function init() {
		const popup = document.querySelector('[data-discount-popup]');
		if (!popup) return;

		// Avoid messing with theme editor (disabled while debugging)
		if (!DEBUG_ALWAYS_SHOW && window.Shopify?.designMode) return;

		if (shouldSuppressPopup()) return;

		const dialog = popup.querySelector('.discount-popup__dialog');
		const step1 = popup.querySelector('[data-discount-step="1"]');
		const step2 = popup.querySelector('[data-discount-step="2"]');
		const step3 = popup.querySelector('[data-discount-step="3"]');
		const optionBtns = popup.querySelectorAll('[data-discount-option]');
		const phoneForm = popup.querySelector('[data-discount-phone-form]');
		const phoneInput = popup.querySelector('#DiscountPopupPhone');
		const phoneSubmitBtn = popup.querySelector('[data-discount-phone-submit]');
		const phoneSkipBtn = popup.querySelector('[data-discount-phone-skip]');
		const phoneStatusEl = popup.querySelector('[data-discount-phone-status]');
		const closeEls = popup.querySelectorAll('[data-discount-popup-close]');
		const nameInput = popup.querySelector('#DiscountPopupName');
		const successCloseBtn = popup.querySelector('[data-discount-success-close]');

		let shown = false;
		let releaseFocusTrap = null;
		let lastActive = null;
		let selectedPreference = null;
		let discountClaimed = false; // Track if user successfully claimed a discount

		function open() {
			if (shown) return;
			shown = true;
			lastActive = document.activeElement;

			popup.classList.add('is-open');
			popup.setAttribute('aria-hidden', 'false');
			document.body.classList.add('overflow-hidden');
			root.style.setProperty(
				'--padding-right',
				`${window.innerWidth - root.clientWidth}px`
			);

			releaseFocusTrap = trapFocus(dialog);

			// Focus the first option button
			const firstOption = popup.querySelector('[data-discount-option]');
			if (firstOption) firstOption.focus();
		}

		function showStep(n) {
			const steps = [step1, step2, step3];
			steps.forEach((el, idx) => {
				if (!el) return;
				if (idx === n - 1) el.classList.remove('discount-popup__step--hidden');
				else el.classList.add('discount-popup__step--hidden');
			});

			if (releaseFocusTrap) releaseFocusTrap();
			releaseFocusTrap = trapFocus(dialog);

			if (n === 1) {
				const firstOption = popup.querySelector('[data-discount-option]');
				if (firstOption) firstOption.focus();
			} else if (n === 2) {
				if (nameInput) nameInput.focus();
			} else if (n === 3) {
				if (successCloseBtn) successCloseBtn.focus();
			}
		}

		function close() {
			popup.classList.remove('is-open');
			popup.setAttribute('aria-hidden', 'true');
			document.body.classList.remove('overflow-hidden');
			root.style.removeProperty('--padding-right');

			if (releaseFocusTrap) {
				releaseFocusTrap();
				releaseFocusTrap = null;
			}

			if (lastActive && typeof lastActive.focus === 'function') {
				lastActive.focus();
			}

			// Mark state: if they claimed a discount, never show again
			// Otherwise, show again after DISMISSED_SHOW_AGAIN_DAYS
			markPopupState(discountClaimed);

			// Reset to step 1 for next time (in debug mode)
			if (DEBUG_ALWAYS_SHOW) {
				setTimeout(() => {
					showStep(1);
					discountClaimed = false; // Reset for debug mode
				}, 300);
			}
		}

		function goToPhoneStep(preference) {
			selectedPreference = preference || null;
			savePreference(preference);
			showStep(2);
		}

		function setSubmitEnabled(isEnabled) {
			if (!phoneSubmitBtn) return;
			phoneSubmitBtn.disabled = !isEnabled;
		}

		function updateFormValidationUi() {
			const phoneValid = isValidPhone(phoneInput?.value || '');
			const nameValid = isValidName(nameInput?.value || '');
			const allValid = phoneValid && nameValid;
			setSubmitEnabled(allValid);

			// Clear error messaging while the user edits
			if (phoneInput) {
				phoneInput.removeAttribute('aria-invalid');
			}
			if (nameInput) {
				nameInput.removeAttribute('aria-invalid');
			}
			if (phoneStatusEl) phoneStatusEl.textContent = '';
		}

		function maybeShowAfterDelay() {
			if (shown) return;
			// Re-check at open time: the cart's inline capture card may have
			// claimed the identity after this page's initial suppression check.
			if (shouldSuppressPopup()) return;
			open();
		}

		// Close button handlers
		closeEls.forEach((el) => el.addEventListener('click', close));

		// Backdrop click to close
		popup.addEventListener('click', (e) => {
			if (e.target === popup) close();
		});

		// Escape key to close
		document.addEventListener('keydown', (e) => {
			if (!popup.classList.contains('is-open')) return;
			if (e.key === 'Escape') close();
		});

		// Option button handlers (step 1 → step 2)
		optionBtns.forEach((btn) => {
			btn.addEventListener('click', () => {
				const preference = btn.getAttribute('data-discount-option');
				// Track the preference selection for analytics
				trackAnalytics(preference);
				goToPhoneStep(preference);
			});
		});

		// Phone capture (step 2 → step 3)
		if (phoneForm) {
			// Disable submit until form is valid
			updateFormValidationUi();
			if (phoneInput) {
				phoneInput.addEventListener('input', updateFormValidationUi);
				phoneInput.addEventListener('change', updateFormValidationUi);
			}
			if (nameInput) {
				nameInput.addEventListener('input', updateFormValidationUi);
				nameInput.addEventListener('change', updateFormValidationUi);
			}

			phoneForm.addEventListener('submit', (e) => {
				e.preventDefault();
				const phoneValue = phoneInput?.value || '';
				const nameValue = nameInput?.value || '';

				if (!isValidName(nameValue)) {
					if (phoneStatusEl) {
						phoneStatusEl.textContent = 'Please enter your name.';
					}
					nameInput?.setAttribute('aria-invalid', 'true');
					nameInput?.focus();
					setSubmitEnabled(false);
					return;
				}

				if (!isValidPhone(phoneValue)) {
					if (phoneStatusEl) {
						phoneStatusEl.textContent =
							'Please enter a valid 10-digit phone number.';
					}
					phoneInput?.setAttribute('aria-invalid', 'true');
					phoneInput?.focus();
					setSubmitEnabled(false);
					return;
				}

				nameInput?.removeAttribute('aria-invalid');
				phoneInput?.removeAttribute('aria-invalid');

				saveName(nameValue);
				savePhone(phoneValue);
				// Tell cart-recovery.js the identity is captured so it can hide
				// its inline capture card and enroll the current cart right away.
				document.dispatchEvent(new CustomEvent('mellow:identity-captured'));
				if (selectedPreference) trackLead(selectedPreference, phoneValue);

				if (phoneSubmitBtn) {
					phoneSubmitBtn.disabled = true;
					phoneSubmitBtn.textContent = 'Generating...';
				}

				// Generate the Shopify discount code (via Cloudflare Worker)
				createDiscountCode(selectedPreference, phoneValue, nameValue)
					.then(async (result) => {
						if (result?.ok) {
							// Success - mark as claimed so popup never shows again
							discountClaimed = true;
							showStep(3);
							return;
						}

						// Handle specific error cases
						if (result?.error === 'already_claimed') {
							// They've already claimed a discount with this phone - mark as claimed
							// so popup never shows again
							discountClaimed = true;
							if (phoneStatusEl) {
								phoneStatusEl.innerHTML =
									'A discount code has already been sent to this number.<br>Please check your WhatsApp!';
							}
							if (phoneSubmitBtn) {
								phoneSubmitBtn.disabled = false;
								phoneSubmitBtn.textContent = 'Send on WhatsApp';
							}
							return;
						}

						// Generic error
						if (phoneStatusEl) {
							phoneStatusEl.textContent =
								"Thanks! We couldn't generate a code right now. Please try again in a moment.";
						}
						if (phoneSubmitBtn) {
							phoneSubmitBtn.disabled = false;
							phoneSubmitBtn.textContent = 'Send on WhatsApp';
						}
					})
					.catch(() => {
						if (phoneStatusEl) {
							phoneStatusEl.textContent =
								"Thanks! We couldn't generate a code right now. Please try again in a moment.";
						}
						if (phoneSubmitBtn) {
							phoneSubmitBtn.disabled = false;
							phoneSubmitBtn.textContent = 'Send on WhatsApp';
						}
					});
			});
		}

		if (phoneSkipBtn) {
			phoneSkipBtn.addEventListener('click', () => {
				close();
			});
		}

		// Success step close button handler
		if (successCloseBtn) {
			successCloseBtn.addEventListener('click', () => {
				close();
			});
		}

		// Show popup after delay
		if (DEBUG_ALWAYS_SHOW) {
			window.setTimeout(open, 150);
		} else {
			window.setTimeout(maybeShowAfterDelay, DEFAULT_DELAY_MS);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
